// ─────────────────────────────────────────────────────────────────────────────
// PARTNER LEADS — the front of the funnel.
//
// 🔑 partner_leads → partner_invites → partner_users. Everything BEFORE
// registration lives here; Partners and Accounts now mean REGISTERED. The lead
// row closes when the partner registers and never lives alongside them.
//
// ⚠️ THE PAGE THIS REPLACED WAS A CALENDLY LIST. It showed four statuses —
// new / booked / cancelled / converted — because that is what the webhook wrote,
// so a lead nobody had booked a call with had no representable state and a
// cancelled meeting looked like a dead lead. Stage and meeting are two facts now;
// see backend/constants/partnerLeadStage.js for why.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useTableSort } from '../hooks/useTableSort';
import PartnerInviteModal from '../components/PartnerInviteModal';
import LeadImportModal from '../components/LeadImportModal';
import { displayPhone, telHref } from '../utils/phone';
// ⚠️ THE LIST THAT ALREADY EXISTS. Its own header records why: license_state
// came to hold both 'TX' and 'Tx' because the partner forms were free text, and
// the Partners table's State filter offered them as two different states.
import { US_STATES } from '../constants/usStates';
import { SOURCE_LABEL } from '../constants/leadSources';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ⚠️ TRANSCRIBED FROM backend/constants/partnerLeadStage.js. There is no import
// path between frontend/ and backend/, so this is a checked copy — the parity
// test fails if the two lists drift.
const STAGES = [
  { key: 'new',              label: 'New',              bg: '#edf1fb', color: '#3a6ad9' },
  { key: 'contacted',        label: 'Contacted',        bg: '#eff6ff', color: '#2854c0' },
  { key: 'discovery_booked', label: 'Discovery booked', bg: '#fffbeb', color: '#7a5200' },
  { key: 'discovery_held',   label: 'Discovery held',   bg: '#f4fee8', color: '#2d6a0a' },
  { key: 'invited',          label: 'Invited',          bg: '#f5f3ff', color: '#6d28d9' },
  // ⚠️ THE KEY IS STILL `registered`; ONLY THE LABEL IS "Converted". It is in
  // the CHECK constraint, on five production rows, and written by registration.
  // Renaming the key would be a migration to change one word on a screen.
  { key: 'registered',       label: 'Converted',        bg: '#acf44e', color: '#1a1a1a' },
  // 🚨 REPLACED `not_interested` AND `unresponsive`, which both meant "stop
  // working this" and neither of which was ever set — zero rows in six months.
  { key: 'archived',         label: 'Archived',         bg: '#f3f4f6', color: '#6b7280' },
];
// Hidden unless explicitly asked for — a converted lead is a partner now, and an
// archived one was put down on purpose. Mirrors the server's default.
const CLOSED_STAGE_KEYS = ['registered', 'archived'];
const STAGE = Object.fromEntries(STAGES.map((s) => [s.key, s]));
// 🔑 THE TWO A REP CANNOT SET BY HAND. `invited` is reached by sending the invite
// and `registered` by the partner registering — the server refuses both, and
// offering them here would be a dropdown that errors.
const REP_SETTABLE = STAGES.filter((s) => !['invited', 'registered'].includes(s.key));

const TYPE_LABEL = { realtor: 'Realtor', loan_officer: 'Mortgage', homebuilder: 'Homebuilder' };
const LEVEL_LABEL = { individual: 'Individual', principal: 'Principal' };
// 🚨 direct_invite WAS MISSING AND THE LEADS THAT NEEDED IT SAID "Organic".
// utils/leadForInvite.js hardcoded lead_source:'organic' on every lead it built
// behind a DoorlyDesk invite — the opposite of the truth, since those leads
// exist only because a named rep pressed Invite. Both production rows read
// "Organic" under notes saying "Invited from DoorlyDesk by Nick Covington."
// SOURCE_LABEL moved to constants/leadSources.js — the Partners drawer needs
// the same labels, and two copies of a label map is how one stops matching.


const fmtDate = (dt) => (dt ? new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
// 🔑 THE ONLY THING THE PAGE DECIDES IS THE COLOUR. What each key MEANS is
// settled in backend/constants/marketExpansion.js, which is why there is no
// state list here to fall out of date.
const STANDING_TONE = {
  licensed: { background: '#ecfdf5', color: '#047857' },   // issued — work it
  pending:  { background: '#fefce8', color: '#a16207' },   // accepted, licence not issued
  waiting:  { background: '#fff7ed', color: '#c2410c' },   // outside the footprint, held
  unknown:  { background: '#f3f4f6', color: '#6b7280' },
};

const fmtWhen = (dt) => (dt ? new Date(dt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

// ⚠️ DAYS SINCE, NOT A DATE, on the column a rep scans for neglect. "14 Jan" asks
// them to do arithmetic; "31d ago" is the thing they are actually looking for.
function sinceLabel(dt) {
  if (!dt) return { text: 'Never', stale: true };
  const days = Math.floor((Date.now() - new Date(dt).getTime()) / 86400000);
  if (days <= 0) return { text: 'Today', stale: false };
  if (days === 1) return { text: 'Yesterday', stale: false };
  return { text: `${days}d ago`, stale: days >= 14 };
}

function Pill({ stage }) {
  const s = STAGE[stage] || { label: stage || '—', bg: '#f3f4f6', color: '#6b7280' };
  return (
    <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 9999, fontSize: 11,
      fontWeight: 700, background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>{s.label}</span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// THE CONTACT CARD — READ, THEN EDIT.
//
// 🚨 A LEAD'S DETAILS ARRIVE FROM SOMEBODY ELSE'S TYPING. A broker filling in an
// invite, a webflow form, a spreadsheet column that was one to the left — all
// three get things wrong, and correcting a transposed phone number meant a SQL
// console before this.
//
// ⚠️ EVERY FIELD AT ONCE, NOT ONE AT A TIME. Five inline inputs saving on blur
// is five chances to half-apply an edit, and the email uniqueness check on the
// server can refuse — which must refuse the WHOLE edit rather than leave the
// phone changed and the address not.
//
// ⚠️ AND THE DRAFT IS DISCARDED ON CANCEL, DELIBERATELY. A rep who opens the
// editor, types over an address and thinks better of it should get the record
// back, not their half-typed version.
// ─────────────────────────────────────────────────────────────────────────────
function ContactCard({ lead, sel, busy, onSave }) {
  const [editing, setEditing] = React.useState(false);
  const blank = React.useCallback(() => ({
    name: lead.name || '', email: lead.email || '', phone: lead.phone || '',
    state: lead.state || '', company: lead.company || '',
    partner_type: lead.partner_type || '', level: lead.level || '',
  }), [lead]);
  const [form, setForm] = React.useState(blank);
  const [error, setError] = React.useState(null);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const start = () => { setForm(blank()); setError(null); setEditing(true); };
  const cancel = () => { setForm(blank()); setError(null); setEditing(false); };
  const save = async () => {
    setError(null);
    // ⚠️ ONLY WHAT CHANGED. Sending every field would rewrite a value another
    // rep edited while this panel was open, and would make the server's email
    // uniqueness check run on an address nobody touched.
    const body = {};
    for (const [k, v] of Object.entries(form)) {
      const was = lead[k] == null ? '' : String(lead[k]);
      if (String(v) !== was) body[k] = v;
    }
    if (!Object.keys(body).length) { setEditing(false); return; }
    const res = await onSave(body);
    if (res && res.error) { setError(res.error); return; }
    setEditing(false);
  };

  const card = { border: '1px solid #f3f4f6', borderRadius: 10, padding: '12px 13px', background: '#fcfcfd' };
  const head = { fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' };
  const btn = { padding: '4px 11px', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer' };
  const field = { ...sel, width: '100%', boxSizing: 'border-box', padding: '6px 9px' };

  if (!editing) {
    return (
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={head}>Contact</span>
          <button type="button" onClick={start}
            style={{ ...btn, background: '#fff', color: '#3a6ad9', border: '1px solid #bfdbfe' }}>
            Edit
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, fontSize: 13 }}>
          <Row label="Name">{lead.name || <span style={{ color: '#d1d5db' }}>—</span>}</Row>
          <Row label="Email">
            {lead.email
              ? <a href={`mailto:${lead.email}`} style={{ color: '#3a6ad9', textDecoration: 'none' }}>{lead.email}</a>
              : <span style={{ color: '#d1d5db' }}>—</span>}
          </Row>
          <Row label="Phone">
            {lead.phone
              ? <a href={telHref(lead.phone)} style={{ color: '#3a6ad9', textDecoration: 'none' }}>{displayPhone(lead.phone)}</a>
              : <span style={{ color: '#d1d5db' }}>—</span>}
          </Row>
          {/* 🔑 STATIC LIKE THE REST. This was a permanently-open input, which
              reads as the one field that is wrong. */}
          <Row label="Company">{lead.company || <span style={{ color: '#d1d5db' }}>—</span>}</Row>
          <Row label="State">
            {lead.state ? (
              /* 🔑 THE SAME SERVER-DERIVED STANDING THE TABLE SHOWS, not a second
                 opinion about which states are licensed. */
              <span title={lead.state_standing_label || ''} style={{
                padding: '1px 6px', borderRadius: 4, fontWeight: 600, fontSize: 11.5,
                ...(STANDING_TONE[lead.state_standing] || STANDING_TONE.waiting),
              }}>{lead.state}</span>
            ) : <span style={{ color: '#d1d5db' }}>—</span>}
          </Row>
          <Row label="Type">
            {TYPE_LABEL[lead.partner_type] || <span style={{ color: '#d1d5db' }}>Unknown</span>}
            {lead.level ? <span style={{ color: '#6b7280' }}> · {LEVEL_LABEL[lead.level]}</span> : null}
          </Row>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...card, background: '#fff', borderColor: '#c5d3f5' }}>
      <div style={{ ...head, marginBottom: 9 }}>Edit contact</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[['name', 'Name', 'Jane Smith'], ['email', 'Email', 'jane@firm.com'],
          ['phone', 'Phone', '512-555-0100'], ['company', 'Company', 'Firm or brokerage']].map(([k, label, ph]) => (
          <label key={k} style={{ display: 'block' }}>
            <span style={{ display: 'block', fontSize: 11, color: '#6b7280', marginBottom: 3 }}>{label}</span>
            <input value={form[k]} placeholder={ph} onChange={set(k)} style={field} />
          </label>
        ))}
        <div style={{ display: 'flex', gap: 8 }}>
          <label style={{ flex: '0 0 120px' }}>
            <span style={{ display: 'block', fontSize: 11, color: '#6b7280', marginBottom: 3 }}>State</span>
            {/* 🚨 A PICKER, NOT A TEXT BOX. Free text here produces 'TX', 'Tx',
                'tx' and 'Texas' as four different states — the exact damage
                constants/usStates.js was written to stop, and it is invisible
                until a filter or a territory report quietly double-counts.
                🔑 THE ABBREVIATION IS THE STORED VALUE; the name is for the
                person reading the list. The server still normalises whatever
                arrives — a select is a convenience, not the constraint. */}
            <select value={form.state} onChange={set('state')} style={field}>
              <option value="">— not set —</option>
              {US_STATES.map(([abbr, name]) => (
                <option key={abbr} value={abbr}>{abbr} — {name}</option>
              ))}
            </select>
          </label>
          <label style={{ flex: 1 }}>
            <span style={{ display: 'block', fontSize: 11, color: '#6b7280', marginBottom: 3 }}>Type</span>
            <select value={form.partner_type} onChange={set('partner_type')} style={field}>
              <option value="">— not set —</option>
              {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label style={{ flex: 1 }}>
            <span style={{ display: 'block', fontSize: 11, color: '#6b7280', marginBottom: 3 }}>Level</span>
            <select value={form.level} onChange={set('level')} style={field}>
              <option value="">— not set —</option>
              {Object.entries(LEVEL_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
        </div>
      </div>
      {/* ⚠️ THE SERVER'S MESSAGE, NOT A GENERIC ONE. "Another open lead already
          uses that address" tells a rep what to do; "could not save" does not. */}
      {error && (
        <div style={{ marginTop: 9, padding: '7px 10px', background: '#fff0f3', color: '#ff082f',
          fontSize: 12, borderRadius: 7 }}>{error}</div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 11 }}>
        <button type="button" onClick={save} disabled={busy}
          style={{ ...btn, background: '#3a6ad9', color: '#fff', border: 'none', padding: '6px 14px' }}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={cancel} disabled={busy}
          style={{ ...btn, background: '#fff', color: '#374151', border: '1px solid #d1d5db', padding: '6px 14px' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// A label/value line in the side panel. Kept as a component so the four rows
// cannot drift apart — the previous panel had no rows at all, and a rep opening
// a lead to make a call could not see the firm or the state.

// ── 🔑 THE EVA TRANSCRIPT ON A PARTNER LEAD ─────────────────────────────────
//
// ⚠️ IT DISTINGUISHES "NO CHAT" FROM "BROKEN LINK". A lead that never came from
// a conversation simply has none; a lead whose source_ref names a session nobody
// can find is a defect, and rendering both as an empty box makes them look the
// same. The server returns a reason for exactly this.
function LeadTranscript({ leadId, token }) {
  const [state, setState] = React.useState({ loading: true, transcript: null, reason: null, error: null });
  React.useEffect(() => {
    let live = true;
    fetch(`${API}/api/admin/partner-leads/${leadId}/transcript`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((j) => { if (live) setState({ loading: false, transcript: j.transcript, reason: j.reason, error: j.error || null }); })
      .catch((e) => { if (live) setState({ loading: false, transcript: null, reason: null, error: e.message }); });
    return () => { live = false; };
  }, [leadId, token]);

  const head = (
    <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
      Eva conversation
    </div>
  );
  if (state.loading) return <div>{head}<div style={{ fontSize: 12, color: '#9ca3af' }}>Loading…</div></div>;
  if (state.error) return <div>{head}<div style={{ fontSize: 12, color: '#b91c1c' }}>{state.error}</div></div>;
  if (!state.transcript) {
    return (
      <div>{head}
        <div style={{ fontSize: 12, color: state.reason === 'session_not_found' ? '#b45309' : '#9ca3af' }}>
          {state.reason === 'session_not_found'
            ? 'This lead names a chat session that could not be found — the link between the lead and the conversation is broken.'
            : 'No conversation recorded for this lead.'}
        </div>
      </div>
    );
  }
  return (
    <div>{head}
      {state.transcript.map((sess) => (
        <div key={sess.session_uuid} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 5 }}>
            {sess.created_at ? new Date(sess.created_at).toLocaleString() : ''} · {sess.message_count} message{sess.message_count === 1 ? '' : 's'}
            {sess.escalated && <span style={{ color: '#b45309', fontWeight: 700 }}> · escalated</span>}
          </div>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, background: '#fafafa' }}>
            {sess.messages.length === 0
              ? <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>Session recorded with no messages stored.</div>
              : sess.messages.map((m, i) => (
                <div key={i} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                    color: m.role === 'user' ? '#1e40af' : '#6b7280', marginBottom: 2 }}>
                    {m.role === 'user' ? 'Them' : 'Eva'}
                  </div>
                  <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{m.content}</div>
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── 🚨 WHAT WE HAVE ACTUALLY SENT THIS LEAD ─────────────────────────────────
//
// The panel could show a rep the conversation that produced a lead and not one
// email Doorly had sent them. A rep about to make a call had no way to know
// whether they were cold or nineteen emails deep — and when the drip went from
// four emails to nineteen, that gap got nineteen times wider.
//
// 🔑 TWO LOGS, BECAUSE THEY ANSWER DIFFERENT QUESTIONS. The LEDGER says which
// step the campaign decided to send. The DELIVERY log says whether it arrived.
// A ledger row with no delivery row means the campaign fired and nothing
// landed, which is invisible if you render only one of them.
//
// ⚠️ FETCHED ON DEMAND, like the transcript. A list must not carry N histories.
function LeadEmailLog({ leadId, token }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/admin/partner-leads/${leadId}/emails`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((b) => { if (alive) { if (b.error) setErr(b.error); else setData(b); } })
      .catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [leadId, token]);

  const head = (
    <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
      Drip emails sent
    </div>
  );
  if (err) return <div>{head}<div style={{ fontSize: 12.5, color: '#b91c1c' }}>{err}</div></div>;
  if (!data) return <div>{head}<div style={{ fontSize: 12.5, color: '#9ca3af' }}>Loading…</div></div>;

  const { steps = [], delivery = [], total } = data;
  // Only the drip categories. Everything else in email_log for this address is
  // transactional — an invite, a password reset — and belongs to a different
  // question than "where are they in the sequence".
  const drip = delivery.filter((d) => d.category === 'partner_lead_drip');
  const fmt = (d) => new Date(d).toLocaleString('en-US',
    { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 16px', background: '#fcfcfd' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
        {head}
        {total ? (
          <div style={{ fontSize: 11, color: '#6b7280' }}>Step {steps.length} of {total}</div>
        ) : null}
      </div>

      {steps.length === 0 ? (
        <div style={{ fontSize: 12.5, color: '#9ca3af', lineHeight: 1.6 }}>
          Nothing sent yet.
          {/* 🔑 NOT ENROLLED AND ENROLLED-BUT-NOT-YET-DUE ARE DIFFERENT, and a
              rep reading this is about to decide whether to call. Saying only
              "nothing sent" would read as the first when it is usually the
              second — the first email lands on day 1. */}
          <div style={{ marginTop: 5, color: '#b0b5bd' }}>
            An enrolled lead receives the first email one day after enrolment.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, maxHeight: 260, overflowY: 'auto' }}>
          {steps.map((st) => {
            // ⚠️ MATCHED ON THE SEND TIME, not on subject. The ledger and the
            // delivery log share no key — email_log has no drip_key — so the
            // only honest join is the minute they were written. A miss shows
            // the ledger row without a delivery badge, which is the true
            // answer: the campaign fired and we cannot confirm it landed.
            const near = drip.find((d) =>
              Math.abs(new Date(d.sent_at) - new Date(st.sent_at)) < 5 * 60 * 1000);
            const ds = near?.delivery_status;
            const tone = !ds ? { c: '#6b7280', b: '#f3f4f6', t: near ? 'sent' : 'no delivery record' }
              : /deliver/i.test(ds) ? { c: '#166534', b: '#dcfce7', t: ds }
              : /bounce|fail|complain/i.test(ds) ? { c: '#b91c1c', b: '#fee2e2', t: ds }
              : { c: '#6b7280', b: '#f3f4f6', t: ds };
            return (
              <div key={st.drip_key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, paddingBottom: 7, borderBottom: '1px solid #f1f2f4' }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, color: '#111827', fontWeight: 600, overflowWrap: 'anywhere' }}>
                    {near?.subject || `Day ${st.sequence_day}`}
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                    Day {st.sequence_day} · {fmt(st.sent_at)}
                  </div>
                  {near?.bounce_reason && (
                    <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 3 }}>{near.bounce_reason}</div>
                  )}
                </div>
                <span style={{ flex: '0 0 auto', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: tone.b, color: tone.c, textTransform: 'capitalize' }}>
                  {tone.t}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
      <span style={{ width: 54, flexShrink: 0, color: '#9ca3af', fontSize: 12 }}>{label}</span>
      <span style={{ color: '#111827', wordBreak: 'break-word' }}>{children}</span>
    </div>
  );
}

// ── 🔑 A CHECKBOX DROPDOWN, NOT <select multiple> ───────────────────────────
//
// A native multiple-select needs ctrl-click to add and silently replaces the
// whole selection on a plain click — so the most common gesture destroys what
// the user already picked, with no affordance saying so. Checkboxes make
// "these three" the obvious action and "one" still a single click.
//
// ⚠️ THE BUTTON STATES THE SELECTION, not a count. "2 selected" makes a rep
// open the menu to find out what they are filtering by, which is the question
// the control exists to answer.
function MultiSelect({ label, options, value, onChange, style, emptyNote }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  const chosen = options.filter((o) => value.includes(o.key));
  const text = chosen.length === 0 ? label
    : chosen.length <= 2 ? chosen.map((o) => o.label).join(', ')
    : `${chosen.length} of ${options.length}`;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        style={{ ...style, cursor: 'pointer', textAlign: 'left', minWidth: 150,
          color: chosen.length ? '#111827' : '#6b7280', fontWeight: chosen.length ? 600 : 400,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
        <span style={{ color: '#9ca3af', fontSize: 10 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', zIndex: 50, top: 'calc(100% + 4px)', left: 0,
          minWidth: 200, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.10)', padding: 6, maxHeight: 280, overflowY: 'auto' }}>
          {chosen.length > 0 && (
            <button type="button" onClick={() => onChange([])}
              style={{ width: '100%', textAlign: 'left', border: 'none', background: 'none',
                cursor: 'pointer', padding: '6px 8px', fontSize: 12, color: '#3a6ad9', fontWeight: 700 }}>
              Clear
            </button>
          )}
          {options.map((o) => (
            <label key={o.key} style={{ display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 8px', fontSize: 13, color: '#111827', cursor: 'pointer', borderRadius: 6 }}>
              <input type="checkbox" checked={value.includes(o.key)} onChange={() => toggle(o.key)} />
              {o.label}
            </label>
          ))}
          {/* ── 🚨 AN UNCHECKED MENU UNDER A LABEL SAYING "ALL" IS A LIE ──────
              With nothing selected the button read "All stages" while the
              server was hiding Converted and Archived — so the control said one
              thing, the table showed another, and the boxes explaining the
              difference were all empty. Nothing was wrong with the filtering;
              the control was describing a state it was not in.

              🔑 SAID PLAINLY INSTEAD. The empty state is a real default with a
              reason, so it gets a sentence rather than a word that contradicts
              it. */}
          {emptyNote && chosen.length === 0 && (
            <div style={{ padding: '8px 8px 4px', marginTop: 4, borderTop: '1px solid #f3f4f6',
              fontSize: 11.5, color: '#9ca3af', lineHeight: 1.5 }}>
              {emptyNote}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, tone, note }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 16px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 900, color: tone || '#111827', lineHeight: 1.2, marginTop: 2 }}>{value}</div>
      {note && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{note}</div>}
    </div>
  );
}

// ── 🔑 THE THREE QUESTIONS THIS PAGE ANSWERS ────────────────────────────────
//
//   TABLE   — "what's in the pipeline"
//   KANBAN  — "where is everything"
//   MY DAY  — "what do I do this morning"
//
// ⚠️ AND THEY ARE NOT THREE FILTERS ON ONE LIST. My Day reads the VIEWER'S OWN id
// server-side and cannot be widened by a parameter — an admin opening it sees
// their own leads, not everyone's. Table and Kanban can legitimately show an admin
// the whole book, because "what is in the pipeline" is a question about everyone's
// work and "what do I do this morning" is not. Same distinction as the Enterprise
// Team view.
const VIEWS = [
  { key: 'table',  label: 'Table' },
  { key: 'kanban', label: 'Board' },
  { key: 'my_day', label: 'My Day' },
];

// 🔑 COLUMNS ARE THE OPEN STAGES ONLY. not_interested, unresponsive and registered
// grow forever — every lead ever worked ends in one of them — so as lanes they
// would push the live work off-screen within a month. They are reachable through
// the stage filter in the table view, where a long list is what you came for.
const BOARD_STAGES = ['new', 'contacted', 'discovery_booked', 'discovery_held', 'invited'];

// ⚠️ PER USER, NOT PER BROWSER. Two reps on one machine should not inherit each
// other's view, and the stored admin user is where the id lives.
const viewKey = () => {
  try {
    const u = JSON.parse(localStorage.getItem('doorly_admin_user') || '{}');
    return `doorly_leads_view_${u.id || 'anon'}`;
  } catch { return 'doorly_leads_view_anon'; }
};

// ── 🔑 THE PANEL'S TABS ─────────────────────────────────────────────────────
//
// The panel was one scrolling column: contact, stage, transcript, invite, type,
// level, owner, notes, a button, Calendly links, and a footer of dates. Eleven
// blocks with nothing grouping them, and the drip log would have made twelve.
//
// Same four tabs the borrower Leads panel uses, because they answer the same
// four questions and a rep should not have to learn two layouts:
//   Overview       — who they are and what we are doing about them
//   Source         — where they came from, including the chat that produced them
//   Communications — everything Doorly has sent, and the controls that send more
//   Activity       — the dates
const LEAD_TABS = [
  ['overview', 'Overview'],
  ['source', 'Source'],
  ['comms', 'Communications'],
  ['activity', 'Activity'],
];

export default function AdminPartnerLeads() {
  const token = localStorage.getItem('doorly_admin_token');
  const adminUser = (() => {
    try { return JSON.parse(localStorage.getItem('doorly_admin_user') || '{}'); } catch { return {}; }
  })();
  // 🔑 THE DEFAULT FOLLOWS THE JOB. A Partner Success rep opens this page to work
  // their morning; an admin opens it to see the pipeline. Whatever they pick
  // afterwards wins and persists.
  const [view, setView] = useState(() => {
    try {
      const saved = localStorage.getItem(viewKey());
      if (saved && VIEWS.some((v) => v.key === saved)) return saved;
    } catch { /* private mode */ }
    const roles = adminUser.roles || [adminUser.role].filter(Boolean);
    return roles.includes('admin') ? 'table' : 'my_day';
  });
  useEffect(() => { try { localStorage.setItem(viewKey(), view); } catch { /* private mode */ } }, [view]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // ⚠️ ARRAYS NOW. Each filter takes a list; the server reads comma-separated
  // values and an empty array means "no filter", which is where the default
  // stage exclusion applies.
  const [stageFilter, setStageFilter] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  // ⚠️ RESET TO Overview WHENEVER THE LEAD CHANGES. Left alone, opening a second
  // lead lands on whatever tab the last one was on — so a rep who was reading a
  // transcript opens the next lead straight into an empty Source tab and
  // concludes that lead has no chat.
  const [leadTab, setLeadTab] = useState('overview');
  useEffect(() => { setLeadTab('overview'); }, [selected?.id]);
  const [busy, setBusy] = useState(null);
  // ⚠️ BOTH MODALS LIVE ON THE PAGE, not inside the header block, so closing one
  // refreshes the list through the same load() every other action uses.
  const [inviteOpen, setInviteOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // 🔑 THE ROSTER, SERVED. An admin reassigning a lead needs every Partner
  // Success user — including one who holds no leads yet, which is exactly when
  // you need to select them, and precisely who a list derived from the rows on
  // screen would omit.
  const [owners, setOwners] = useState([]);
  // ⚠️ AND ONLY FOR SOMEBODY WHO CAN USE IT. Both controls the roster feeds — the
  // owner filter and the per-row reassign — are admin-only, so firing this for a
  // rep is a request that can only ever 403. The endpoint is allow-listed for
  // Partner Success (a reassign attempt is refused by the PATCH route, not by
  // hiding the names), but a call whose result nothing renders is still a call
  // nobody should be making.
  useEffect(() => {
    if (!token || !data?.canSeeAll) return;
    fetch(`${API}/api/admin/partner-leads/owners`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setOwners(d?.owners || []))
      .catch(() => setOwners([]));
  }, [token, data?.canSeeAll]);

  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!token) return;
    const p = new URLSearchParams();
    // 🔑 NO open_only. The server's default already hides Converted and
    // Archived when no stage is named; sending open_only as well re-created the
    // bug it was part of — it ANDed closed_at IS NULL onto an explicit stage
    // selection, so asking for Converted returned nothing.
    if (stageFilter.length) p.set('stage', stageFilter.join(','));
    if (ownerFilter.length) p.set('owner', ownerFilter.join(','));
    if (typeFilter.length) p.set('partner_type', typeFilter.join(','));
    if (search.trim()) p.set('search', search.trim());
    // ⚠️ THE VIEW IS SENT, NOT APPLIED CLIENT-SIDE. My Day's scope is the server's
    // to decide — a client-side narrowing would be a filter the viewer could widen
    // by editing a request, which is the thing this view is not.
    if (view === 'my_day') p.set('view', 'my_day');
    try {
      const r = await fetch(`${API}/api/admin/partner-leads?${p}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(await r.json());
    } catch { setError('Could not load leads.'); }
    setLoading(false);
  }, [token, stageFilter, ownerFilter, typeFilter, search, view]);

  useEffect(() => { load(); }, [load]);

  const unsortedLeads = useMemo(() => data?.leads || [], [data]);
  // 🔑 SORTING — the same hook the Partners and Accounts tables use, so the three
  // pages in this section behave identically. Jesse asked for all three.
  //
  // ⚠️ STAGE SORTS BY ITS PIPELINE ORDER, NOT ALPHABETICALLY. "closed_won" before
  // "discovery_booked" is an accident of spelling; the funnel has an order and
  // constants/partnerLeadStage.js is where it is declared. An alphabetical stage
  // column would be an arrow that lies about what it does.
  const SORT_COLUMNS = useMemo(() => ({
    lead:      (l) => l.name || l.email,
    company:   (l) => l.company,
    type:      (l) => TYPE_LABEL[l.partner_type] || '',
    stage:     { get: (l) => {
      const i = STAGES.findIndex((st) => st.key === l.stage);
      return i < 0 ? null : i;
    }, type: 'number' },
    source:    (l) => l.source,
    owner:     (l) => l.owner_name,
    contacted: { get: (l) => l.last_contacted_at, type: 'date' },
    created:   { get: (l) => l.created_at, type: 'date' },
  }), []);
  const { sortKey, sortDir, toggle: toggleSort, sortRows } = useTableSort(SORT_COLUMNS);
  const leads = useMemo(() => sortRows(unsortedLeads), [unsortedLeads, sortRows]);
  const byStage = data?.by_stage || {};

  // ⚠️ THE CARDS COME FROM THE SERVER'S COUNTS, NOT FROM `leads`. The list is
  // filtered and capped at 500; counting the rows on screen would make every card
  // a description of the current filter rather than of the rep's book.
  const patch = async (id, body) => {
    setBusy(id); setError('');
    try {
      const r = await fetch(`${API}/api/admin/partner-leads/${id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      // ⚠️ THE RESULT IS RETURNED AS WELL AS SHOWN. A caller that owns its own
      // form — the contact editor — needs the refusal beside the field that
      // caused it; "another open lead already uses that address" at the top of
      // the panel is a sentence about an input the rep has to go find.
      if (!r.ok) { setError(j.error || 'Could not save that.'); return { error: j.error || 'Could not save that.' }; }
      if (selected && selected.id === id) setSelected(j.lead);
      await load();
      return { ok: true, lead: j.lead };
    } catch {
      setError('Could not save that.');
      return { error: 'Could not save that.' };
    }
    finally { setBusy(null); }
  };

  // ── 🚨 DRAGGING INTO `invited` SENDS AN INVITE ───────────────────────────
  //
  // The stage is a HANDOFF: once partner_invites holds a row, that table owns the
  // state and the lead points at it. A lead sitting in the invited lane with no
  // invite behind it is the two-records problem wearing a UI affordance — the lane
  // would be lying, and the person it names would be waiting for an email nobody
  // sent. So the board calls a different endpoint for that column, and the PATCH
  // route refuses the stage outright.
  const sendInvite = async (id) => {
    setBusy(id); setError('');
    try {
      const r = await fetch(`${API}/api/admin/partner-leads/${id}/invite`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json();
      if (!r.ok) { setError(j.error || 'Could not send that invitation.'); return false; }
      if (selected && selected.id === id) setSelected(j.lead);
      await load();
      return true;
    } catch { setError('Could not send that invitation.'); return false; }
    finally { setBusy(null); }
  };

  // 🔑 RESEND AND CANCEL, WHICH HAVE HAD NO REACHABLE UI SINCE THE INVITE ROWS
  // LEFT THE PARTNERS PAGE. Both endpoints are live and scoped to the inviting
  // rep; a live endpoint with no control is the shape that left /broker-application
  // routed and linked from nowhere for three weeks.
  const inviteAction = async (lead, action) => {
    if (!lead.invite_id) return;
    setBusy(lead.id); setError('');
    try {
      const r = await fetch(`${API}/api/admin/partners/invites/${lead.invite_id}/${action}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setError(j.error || `Could not ${action} that invitation.`); return; }
      await load();
    } catch { setError(`Could not ${action} that invitation.`); }
    finally { setBusy(null); }
  };

  // ⚠️ A DRAG IS A WRITE, so it goes through the same two endpoints — and the same
  // scoping — as every other change on this page. The board does not get a looser
  // path because the gesture is nicer.
  const [dragId, setDragId] = useState(null);
  const dropOn = async (stage) => {
    const id = dragId; setDragId(null);
    if (!id) return;
    const lead = leads.find((l) => l.id === id);
    if (!lead || lead.stage === stage) return;
    if (stage === 'invited') { await sendInvite(id); return; }
    await patch(id, { stage });
  };

  const th = { padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' };
  const td = { padding: '11px 12px', fontSize: 13, color: '#111827', verticalAlign: 'top' };
  const sel = { padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, background: '#fff' };
  // Shared by the two answers to a passed meeting, so neither reads as the
  // default one — "held it" and "no-show" are both just what happened.
  const dayBtn = { padding: '5px 11px', border: '1px solid', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer' };

  return (
    <AdminLayout>
      <div className="admin-page-body" style={{ maxWidth: 1500, margin: '0 auto' }}>
        {/* ⚠️ THE SAME SHAPE AS PARTNERS AND ACCOUNTS — title left, actions right
            — and now the same RULE, not a third hand-rolled copy of it. The
            three pages stacked, wrapped and squeezed respectively on a phone;
            .admin-page-head in AdminLayout is the one definition they share. */}
        <div className="admin-page-head">
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>Partner Leads</h1>
            <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
              Everything before registration. A lead closes when the partner registers.
              {data?.scoped && ' Showing yours and unclaimed.'}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            {/* 🚨 THE IMPORT BUTTON HAD NO FRONT DOOR. The whole staging →
                mapping → preview → attestation → transactional-commit backend
                shipped and nothing ever rendered a way in, so the only leads
                that could exist were ones typed one at a time or booked through
                Calendly. */}
            <button type="button" onClick={() => setImportOpen(true)}
              style={{ padding: '9px 16px', borderRadius: 9, border: '1px solid #d1d5db', background: '#fff',
                       color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              Import leads
            </button>
            {/* ⚠️ SAME SIZE AND SAME MODAL AS THE PARTNERS PAGE. Not a second
                invite mechanism — components/PartnerInviteModal posts to the one
                endpoint, which writes the invite AND the lead behind it. */}
            <button type="button" onClick={() => setInviteOpen(true)}
              style={{ padding: '9px 18px', borderRadius: 9, border: 'none', backgroundColor: '#3a6ad9',
                       color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                       display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              + Invite Partner
            </button>
          </div>
        </div>

        {/* ⚠️ A SEGMENTED CONTROL, NOT TABS. Tabs imply the same data sliced; these
            are three different questions, and My Day is a different POPULATION
            rather than a narrower one. */}
        <div style={{ display: 'inline-flex', border: '1px solid #e5e7eb', borderRadius: 9, overflow: 'hidden', marginBottom: 16 }}>
          {VIEWS.map((v) => (
            <button key={v.key} type="button" onClick={() => setView(v.key)}
              style={{ padding: '7px 16px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700,
                background: view === v.key ? '#3a6ad9' : '#fff', color: view === v.key ? '#fff' : '#374151' }}>
              {v.label}
            </button>
          ))}
        </div>

        {error && (
          <div style={{ marginBottom: 14, padding: '10px 14px', background: '#fff0f3', color: '#ff082f', fontSize: 13, borderRadius: 8 }}>{error}</div>
        )}

        {/* ── the book, not the filter ────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 18 }}>
          <StatCard label="Open leads" value={data?.open_total ?? '—'} note="Not yet registered or closed" />
          {/* 🚨 THE UNCLAIMED CARD IS GONE WITH THE QUEUE IT COUNTED. Leads are
              assigned when they are created — by the importer, the inviting rep,
              or the territory resolver — so there is no queue to work from.
              ⚠️ ADMINS STILL SEE ORPHANS, because narrowing the scope is what
              made them invisible to everyone else: the card below shows the
              residue, and only to somebody who can act on it. */}
          {/* ── 🚨 TWO CARDS, BECAUSE THERE ARE TWO KINDS OF ORPHAN ────────────
              One card saying "shouldn't happen — check routing" was a false-alarm
              generator, and it would have fired on the FIRST organic booking.
              No rep holds a Calendly link yet, so the booking router sends every
              prospect to the house URL and deliberately leaves the lead unowned
              rather than attaching them to whoever the house calendar answers
              with. That is correct behaviour, it is already alarmed at the
              source, and it is the common case right now — not a fault.
              🔑 THE SERVER READS owner_basis RATHER THAN GUESSING: the booking
              router already recorded 'booking_no_link_anywhere' on the intent,
              and ownerFromToken now carries it through even though there is no
              owner to carry with it. */}
          {data?.canSeeAll && (data?.unowned_expected || 0) > 0 && (
            <StatCard label="Awaiting a booking link" value={data.unowned_expected} tone="#6b7280"
              note="Booked via the house link — assign when a rep has one" />
          )}
          {data?.canSeeAll && ((data?.unowned || 0) - (data?.unowned_expected || 0)) > 0 && (
            <StatCard label="Unassigned" value={(data.unowned || 0) - (data.unowned_expected || 0)}
              tone="#b45309" note="Not accounted for — check routing" />
          )}
          <StatCard label="New" value={byStage.new || 0} />
          <StatCard label="Discovery booked" value={byStage.discovery_booked || 0} />
          <StatCard label="Invited" value={byStage.invited || 0} note="Owned by the invite" />
          {/* ── 🔑 CONVERTED IS THE POINT OF THE FUNNEL ─────────────────────
              A lead reaching `registered` IS the conversion — the row closes
              and a partner_users account exists. It had no card, so the one
              number the page is ultimately about was the one it did not show.

              ⚠️ IT READS by_stage.registered, WHICH USED TO BE STRUCTURALLY 0.
              The counts query was scoped to closed_at IS NULL and registering
              stamps closed_at, so this card would have read zero for ever while
              five leads sat converted. The query dropped that clause; the
              orphan counts kept it. */}
          <StatCard label="Converted" value={byStage.registered || 0} tone="#2d6a0a"
            note="Registered on DoorlyPro" />
        </div>

        {/* ── filters ──────────────────────────────────────────────────────── */}
        {/* ⚠️ NO STAGE OR OWNER FILTER ON MY DAY. The view IS the filter — this
            rep's open leads needing contact — and offering an owner dropdown on it
            would suggest the scope is something the viewer chooses, which is
            exactly what it is not. */}
        {view !== 'my_day' && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email"
            style={{ ...sel, minWidth: 220, flex: '0 1 260px' }} />
          {/* ⚠️ THE LABEL IS "Open stages", NOT "All stages". Nothing selected
              means the server hides Converted and Archived — which is a real
              filter, not the absence of one, and calling it "All" made the
              empty checkboxes look like a bug in the control rather than the
              default doing its job. */}
          <MultiSelect label="Open stages" style={sel} value={stageFilter} onChange={setStageFilter}
            emptyNote={`Showing open stages. ${STAGES.filter((st) => CLOSED_STAGE_KEYS.includes(st.key)).map((st) => st.label).join(' and ')} are hidden until you tick them.`}
            options={STAGES.map((st) => ({ key: st.key, label: st.label }))} />
          {/* ⚠️ THE OWNER FILTER IS AN ADMIN CONTROL. A rep sees only their own
              leads, so filtering by owner would offer them one choice that
              changes nothing and several that return empty. */}
          {data?.canSeeAll && (
            <MultiSelect label="Any owner" style={sel} value={ownerFilter} onChange={setOwnerFilter}
              options={[
                ...owners.map((o) => ({ key: String(o.id), label: o.name || o.email })),
                // 🚨 THE ORPHAN VIEW. Two paths still produce an unowned lead — a
                // booking where no rep holds a link, and a role with no active
                // staff — plus any row predating assignment-on-create.
                { key: 'unowned', label: '— Unassigned —' },
              ]} />
          )}
          <MultiSelect label="Any type" style={sel} value={typeFilter} onChange={setTypeFilter}
            options={Object.entries(TYPE_LABEL).map(([k, v]) => ({ key: k, label: v }))} />
          {/* ── 🚨 THE "Open only" CHECKBOX IS GONE, AND IT WAS THE BUG ────────
              It sent open_only=true, which ANDed closed_at IS NULL onto whatever
              stage was selected — so picking Converted returned nothing at all,
              because registering stamps closed_at. Five converted leads were
              unreachable through the one control built to reach them.

              🔑 THE DEFAULT IS NOW A STAGE EXCLUSION the server owns: no stage
              selected hides Converted and Archived; selecting one shows exactly
              that one. The checkbox has nothing left to express, and a control
              that duplicates a default is the next thing to disagree with it. */}
        </div>
        )}

        {/* ── 🔑 THE BOARD ─────────────────────────────────────────────────── */}
        {view === 'kanban' && (
          <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8, alignItems: 'flex-start' }}>
            {BOARD_STAGES.map((k) => {
              const col = leads.filter((l) => l.stage === k && !l.closed_at);
              const meta = STAGE[k];
              return (
                <div key={k}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => dropOn(k)}
                  style={{ flex: '0 0 250px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: 10, minHeight: 160 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: meta.color }}>{meta.label}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af' }}>{col.length}</span>
                  </div>
                  {/* 🚨 THE INVITED LANE SAYS WHAT DROPPING HERE DOES. A gesture
                      that sends an email to a named person should not be silent
                      about it. */}
                  {k === 'invited' && (
                    <div style={{ fontSize: 10.5, color: '#6d28d9', background: '#f5f3ff', borderRadius: 6, padding: '4px 7px', marginBottom: 8, lineHeight: 1.5 }}>
                      Dropping here sends the invitation.
                    </div>
                  )}
                  {col.length === 0 && (
                    <div style={{ fontSize: 12, color: '#c7cbd1', textAlign: 'center', padding: '18px 0' }}>—</div>
                  )}
                  {col.map((l) => (
                    <div key={l.id} draggable
                      onDragStart={() => setDragId(l.id)}
                      onDragEnd={() => setDragId(null)}
                      onClick={() => setSelected(l)}
                      style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 9, padding: '9px 10px',
                        marginBottom: 8, cursor: 'grab', opacity: busy === l.id ? 0.5 : 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{l.name || l.email}</div>
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                        {TYPE_LABEL[l.partner_type] || 'Type unknown'}
                        {l.level ? ` · ${LEVEL_LABEL[l.level]}` : ''}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 5 }}>
                        <span style={{ fontSize: 11, color: l.owner_name ? '#9ca3af' : '#7a5200', fontWeight: l.owner_name ? 400 : 700 }}>
                          {l.owner_name || 'Unassigned'}
                        </span>
                        <span style={{ fontSize: 11, color: sinceLabel(l.last_contacted_at).stale ? '#b45309' : '#9ca3af' }}>
                          {sinceLabel(l.last_contacted_at).text}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* ⚠️ THE TERMINALS ARE NOT LANES. not_interested, unresponsive and
            registered grow forever — every lead ever worked ends in one — so as
            columns they would push the live work off-screen within a month. They
            are reachable from the table's stage filter, where a long list is what
            you came for. */}
        {view === 'kanban' && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#9ca3af' }}>
            Closed and terminal leads aren&rsquo;t shown on the board —{' '}
            {/* ⚠️ SELECTS THE CLOSED STAGES rather than unchecking a box that no
                longer exists — and it is now a better link than it was, because
                it lands on exactly Converted and Archived instead of on the
                whole table with one filter relaxed. */}
            <button type="button" onClick={() => { setView('table'); setStageFilter(CLOSED_STAGE_KEYS); }}
              style={{ background: 'none', border: 'none', color: '#3a6ad9', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
              see them in the table
            </button>.
          </div>
        )}

        {/* ── 🚨 THE MEETING THAT ALREADY HAPPENED ─────────────────────────── */}
        {/*
            A lead sits at discovery_booked from the moment Calendly says a call
            is booked. Nothing moves it afterwards: the webhook fires on
            invitee.created and invitee.canceled, and a call that simply HAPPENS
            emits neither. So the stage is accurate right up until the meeting
            starts, and from the minute it ends it is a claim about the future
            that has quietly stopped being true.

            🔑 THIS PROMPTS; IT DOES NOT DECIDE. discovery_held means a rep sat on
            a call with this person, and no clock knows that. A booking that
            passed could have been held, no-showed, cut short, or moved by a
            reschedule this list has not caught up with — advancing on elapsed
            time alone would put "had a discovery call" against people nobody has
            ever spoken to, and every count built on that stage would inherit it.

            ⚠️ AND NOT ON CALENDLY'S NO-SHOW EVENT EITHER, though it exists.
            invitee_no_show.created fires when somebody TICKS A BOX in Calendly.
            A rep who reliably ticked that box would already be moving the stage;
            one who does not would make its ABSENCE look like evidence the call
            was held. An unticked box is not a held meeting — it is an unticked
            box.

            ⚠️ AND IT IS MY DAY ONLY. This is a question put to the person who
            owns the answer. On the table it would be an unanswerable prompt
            attached to somebody else's call.
        */}
        {view === 'my_day' && !loading && (() => {
          const now = Date.now();
          const passed = leads.filter((l) => l.stage === 'discovery_booked'
            && l.start_time && new Date(l.start_time).getTime() < now);
          if (!passed.length) return null;
          return (
            <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12,
              padding: '12px 14px', marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#92400e', marginBottom: 2 }}>
                {passed.length === 1 ? 'A call has passed' : `${passed.length} calls have passed`}
              </div>
              <div style={{ fontSize: 12, color: '#a16207', marginBottom: 10 }}>
                Still showing as booked. Did {passed.length === 1 ? 'it' : 'they'} happen?
              </div>
              {passed.map((l) => (
                <div key={l.id} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
                  padding: '7px 0', borderTop: '1px solid #fde68a' }}>
                  <button type="button" onClick={() => setSelected(l)}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      fontSize: 13, fontWeight: 600, color: '#78350f', textAlign: 'left' }}>
                    {l.name || l.email}
                  </button>
                  <span style={{ fontSize: 11.5, color: '#a16207' }}>{fmtWhen(l.start_time)}</span>
                  <div style={{ flex: 1 }} />
                  <button type="button" disabled={busy === l.id}
                    onClick={() => patch(l.id, { stage: 'discovery_held', contacted: true })}
                    style={{ ...dayBtn, background: '#047857', color: '#fff', borderColor: '#047857' }}>
                    Held it
                  </button>
                  {/* 🔑 A NO-SHOW GOES BACK TO `contacted`, WHICH IS WHERE THE
                      CALENDLY CANCELLATION ALSO LANDS. They are the same
                      situation from the lead's point of view: a live prospect
                      with no call behind them and a follow-up owed. It is NOT
                      `unresponsive` — that is a terminal, and one missed meeting
                      is not the end of a relationship.

                      ⚠️ AND IT DOES NOT TOUCH last_contacted_at. Nobody was
                      contacted; marking it would push them down a queue sorted
                      by exactly that. */}
                  <button type="button" disabled={busy === l.id}
                    onClick={() => patch(l.id, { stage: 'contacted' })}
                    style={{ ...dayBtn, background: '#fff', color: '#92400e', borderColor: '#fcd34d' }}>
                    No-show
                  </button>
                </div>
              ))}
            </div>
          );
        })()}

        {/* ── the table ────────────────────────────────────────────────────── */}
        {view !== 'kanban' && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f3f4f6', background: '#fafbfc' }}>
                  {/* 🔑 THE FIRM is one of these. partner_invites already collected
                      it and partner_leads had nowhere to put it, so the page could
                      show a name and an email and nothing about who they work for. */}
                  {[['Lead', 'lead'], ['Company', 'company'], ['Type', 'type'],
                    ['Stage', 'stage'], ['Source', 'source'], ['Owner', 'owner'],
                    ['Last contacted', 'contacted'], ['Created', 'created']].map(([h, key]) => (
                    <th key={key} onClick={() => toggleSort(key)}
                      style={{ ...th, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
                        color: sortKey === key ? '#3a6ad9' : undefined }}>
                      {h}
                      <span style={{ marginLeft: 4, color: sortKey === key ? '#3a6ad9' : '#d1d5db' }}>
                        {sortKey === key ? (sortDir === 'desc' ? '▼' : '▲') : '⇅'}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td style={{ ...td, textAlign: 'center', color: '#9ca3af', padding: 32 }} colSpan={8}>Loading…</td></tr>
                )}
                {!loading && leads.length === 0 && (
                  <tr><td style={{ ...td, textAlign: 'center', color: '#9ca3af', padding: 32 }} colSpan={8}>
                    No leads match these filters.
                  </td></tr>
                )}
                {!loading && leads.map((l) => {
                  const since = sinceLabel(l.last_contacted_at);
                  return (
                    <tr key={l.id} onClick={() => setSelected(l)}
                      style={{ borderBottom: '1px solid #f9fafb', cursor: 'pointer',
                        background: l.closed_at ? '#fcfcfd' : '#fff' }}>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{l.name || '—'}</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>{l.email}</div>
                        {l.phone && (
                          <a href={telHref(l.phone)} onClick={(e) => e.stopPropagation()}
                            style={{ fontSize: 12, color: '#3a6ad9', textDecoration: 'none' }}>{displayPhone(l.phone)}</a>
                        )}
                      </td>
                      <td style={td}>
                        {l.company
                          ? <div style={{ color: '#374151' }}>{l.company}</div>
                          : <span style={{ color: '#d1d5db' }}>—</span>}
                      </td>
                      <td style={td}>
                        {/* 🔑 TYPE AND LEVEL TOGETHER. An AE talking to a brokerage
                            owner runs a different conversation from one talking to
                            an agent, and the pair is what says which. */}
                        <div>{TYPE_LABEL[l.partner_type] || <span style={{ color: '#d1d5db' }}>Unknown</span>}</div>
                        {l.level && <div style={{ fontSize: 12, color: '#6b7280' }}>{LEVEL_LABEL[l.level]}</div>}
                        {/* 🚨 OUT OF FOOTPRINT IS A DIFFERENT CONVERSATION, NOT A
                            WORSE LEAD. A rep who cannot see it at a glance works
                            a Nashville realtor as though a deal were possible
                            this month.

                            🔑 AND THE COLOUR IS NOT A HARDCODED 'TX'. It was,
                            for one commit, and it would have gone stale the day
                            the Florida license is issued — failing in the worse
                            direction by telling a rep a LICENSED state is out of
                            footprint. The standing is derived server-side from
                            constants/marketExpansion.js, the same file the help
                            articles and the license table read: one list, several
                            readers.

                            ⚠️ THREE OUTCOMES, BECAUSE LICENSED IS NOT LENDABLE.
                            Florida is accepted by the program while its license
                            is still pending, and that gap is named in the source
                            rather than tolerated. A green/amber boolean would
                            have to pick one of those facts and discard the
                            other. */}
                        {l.state && (
                          <div style={{ fontSize: 11, marginTop: 2 }}>
                            <span title={l.state_standing_label || ''} style={{
                              padding: '1px 6px', borderRadius: 4, fontWeight: 600,
                              ...(STANDING_TONE[l.state_standing] || STANDING_TONE.waiting),
                            }}>{l.state}</span>
                          </div>
                        )}
                      </td>
                      <td style={td}>
                        <Pill stage={l.stage} />
                        {/* 🔑 ONCE AN INVITE EXISTS, THAT TABLE IS THE AUTHORITY.
                            The lead points at it and shows what actually happened
                            rather than its own stale guess. */}
                        {l.invite_id && l.invite_status && (
                          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>invite {l.invite_status}</div>
                        )}
                        {l.stage === 'discovery_booked' && l.start_time && (
                          <div style={{ fontSize: 11, color: '#7a5200', marginTop: 3 }}>{fmtWhen(l.start_time)}</div>
                        )}
                      </td>
                      <td style={{ ...td, color: '#374151' }}>{SOURCE_LABEL[l.lead_source] || l.lead_source || '—'}</td>
                      <td style={td}>
                        {/* ⚠️ Unassigned IS AN ANOMALY HERE, NOT A QUEUE. Every
                            path assigns an owner at creation, so a blank one is a
                            routing failure — which is why it reads amber and has
                            no action beside it. Admins reassign from the detail
                            panel; a rep cannot, and showing them a control that
                            403s would be worse than showing them nothing. */}
                        {l.owner_name
                          ? <span style={{ color: '#374151' }}>{l.owner_name}</span>
                          : <span style={{ color: '#b45309', fontWeight: 600 }}>Unassigned</span>}
                        {l.owner_basis && <div style={{ fontSize: 11, color: '#9ca3af' }}>{l.owner_basis.replace(/_/g, ' ')}</div>}
                      </td>
                      <td style={{ ...td, color: since.stale ? '#b45309' : '#374151', fontWeight: since.stale ? 600 : 400 }}>
                        {since.text}
                      </td>
                      <td style={{ ...td, color: '#6b7280' }}>{fmtDate(l.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* ── detail ───────────────────────────────────────────────────────── */}
        {selected && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 9000, display: 'flex', justifyContent: 'flex-end' }}>
            <div onClick={() => setSelected(null)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} />
            {/* ⚠️ A COLUMN, NOT ONE SCROLLING BOX. The header and the tab bar
                hold their own height and only the BODY scrolls, so the close
                button and the tabs can never be scrolled away — the same fix
                the borrower Leads panel needed. 100dvh (not %) so mobile
                browser chrome does not push the bottom below the fold. */}
            <div style={{ position: 'relative', width: 'min(460px, 100%)', background: '#fff',
              height: '100dvh', maxHeight: '100dvh', boxShadow: '-8px 0 30px rgba(0,0,0,0.12)',
              display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ flex: '0 0 auto', padding: '22px 22px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                {/* ⚠️ THE SUBHEADER IS THE COMPANY NOW, NOT THE CONTACT DETAILS.
                    Email and phone moved into the Contact card below, and
                    repeating them here put the same two lines on screen twice —
                    which reads as two different facts until you look closely.
                    The firm is the one thing about a lead that belongs beside
                    their name and is nowhere else at the top. */}
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>{selected.name || selected.email}</div>
                  {selected.company && (
                    <div style={{ fontSize: 13, color: '#6b7280' }}>{selected.company}</div>
                  )}
                </div>
                <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', fontSize: 20, color: '#9ca3af', cursor: 'pointer' }}>×</button>
              </div>

              {/* ⚠️ OUTSIDE THE SCROLLING BODY, like the header. A tab bar that
                  scrolls away is a tab bar somebody loses. */}
              <div style={{ flex: '0 0 auto', display: 'flex', gap: 2, padding: '14px 22px 0',
                            borderBottom: '1px solid #e5e7eb' }}>
                {LEAD_TABS.map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setLeadTab(k)}
                    style={{ border: 'none', background: 'none', cursor: 'pointer',
                      padding: '10px 12px', fontSize: 12, fontWeight: 700,
                      color: leadTab === k ? '#3a6ad9' : '#6b7280',
                      borderBottom: `2px solid ${leadTab === k ? '#3a6ad9' : 'transparent'}`,
                      marginBottom: -1 }}>
                    {label}
                  </button>
                ))}
              </div>

              {/* overscrollBehavior:contain stops a scroll that reaches the end
                  of this panel from continuing on the page behind it. */}
              <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto',
                overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch',
                padding: '18px 22px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* ── 🚨 CONTACT AND COMPANY, WHICH THE PANEL NEVER SHOWED ──────
                    The header carried a name, an email and a phone number and the
                    rest of the panel went straight to stage and invite controls.
                    A rep opening a lead to make a call could not see the firm, the
                    state, or how to reach them any other way — so the one screen
                    built for working a lead answered none of the questions asked
                    before dialling.

                    🚨 READ-ONLY UNTIL EDIT IS PRESSED. The first version left
                    Company as a permanently-open input among five static rows,
                    which reads as the one field that is wrong — and an always-live
                    text box next to a phone number invites a stray keystroke into
                    a record a rep only meant to read. One Edit button, every field
                    at once, an explicit Save.

                    ⚠️ AND ONE PATCH, NOT ONE PER FIELD. Saving five separate
                    requests means five chances to half-apply, and the email check
                    on the server can refuse the whole edit rather than leaving the
                    phone number changed and the address not. */}
                {leadTab === 'overview' && (<>
                <ContactCard
                  key={`contact-${selected.id}`}
                  lead={selected}
                  sel={sel}
                  busy={busy === selected.id}
                  onSave={(patchBody) => patch(selected.id, patchBody)}
                />

                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>Stage</label>
                  {/* ⚠️ THE TWO HANDOFF STAGES ARE NOT OFFERED. The server refuses
                      them, so listing them would be a dropdown that errors. */}
                  <select value={REP_SETTABLE.some((s) => s.key === selected.stage) ? selected.stage : ''}
                    disabled={!REP_SETTABLE.some((s) => s.key === selected.stage)}
                    onChange={(e) => patch(selected.id, { stage: e.target.value })}
                    style={{ ...sel, width: '100%', marginTop: 4 }}>
                    {!REP_SETTABLE.some((s) => s.key === selected.stage) && (
                      <option value="">{STAGE[selected.stage]?.label || selected.stage} — set by the system</option>
                    )}
                    {REP_SETTABLE.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>

                {/* ── 🔑 THE CONVERSATION THAT PRODUCED THIS LEAD ───────────────
                    A partner lead captured through Eva carries the session uuid
                    in source_ref. Until this existed the panel could say a lead
                    came from a chat without being able to show the chat — and
                    the chat is usually the only record of what they actually
                    asked for. Rick Baker's said he was a 15-year Coldwell Banker
                    realtor referred by his loan officer; none of that was
                    anywhere a rep could read it.

                    ⚠️ FETCHED ON DEMAND. A list must not carry N conversations. */}
                </>)}

                {/* ── SOURCE ─────────────────────────────────────────────── */}
                {leadTab === 'source' && (<>
                  {selected.source_ref ? (
                    <LeadTranscript key={`tx-${selected.id}`} leadId={selected.id} token={token} />
                  ) : (
                    // 🔑 SAYING SO IS THE ANSWER. An empty tab reads as a
                    // failure to load; most partner leads simply did not come
                    // from a chat.
                    <div style={{ fontSize: 12.5, color: '#9ca3af', lineHeight: 1.6 }}>
                      This lead did not come from an Eva conversation, so there is no transcript.
                    </div>
                  )}

                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 16px', background: '#fcfcfd', fontSize: 12.5, color: '#374151', lineHeight: 1.9 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                      How they arrived
                    </div>
                    <div>Channel: {SOURCE_LABEL[selected.lead_source] || selected.lead_source || '—'}</div>
                    {selected.source && <div>Form: {selected.source}</div>}
                    {selected.routed_via && <div>Routed via: {selected.routed_via}</div>}
                    {selected.owner_basis && <div>Owner assigned by: {selected.owner_basis}</div>}
                  </div>
                </>)}

                {/* ── COMMUNICATIONS ─────────────────────────────────────── */}
                {leadTab === 'comms' && (<>
                  {/* 🚨 THE DRIP LOG. See LeadEmailLog for why the ledger and
                      the delivery record are both needed. */}
                  <LeadEmailLog key={`em-${selected.id}`} leadId={selected.id} token={token} />


                {/* ── 🚨 THE INVITE, AND THE CONTROLS THAT HAD NO HOME ──────────
                    Resend and Cancel have been live endpoints with no reachable UI
                    since the invite rows left the Partners page. They belong where
                    `invited` now lives. A live endpoint with no control is the
                    shape that left /broker-application routed and linked from
                    nowhere for three weeks — harmless at the time, too. */}
                {selected.invite_id ? (
                  <div style={{ border: '1px solid #ede9fe', background: '#faf8ff', borderRadius: 10, padding: '11px 12px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#6d28d9', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Invitation
                    </div>
                    {/* 🔑 THE INVITE IS THE AUTHORITY ONCE IT EXISTS — this reads
                        partner_invites.status, not the lead's own guess. */}
                    <div style={{ fontSize: 13, color: '#111827', marginTop: 3 }}>
                      {selected.invite_status || 'sent'}
                      {selected.invite_sent_at ? ` · ${fmtDate(selected.invite_sent_at)}` : ''}
                    </div>
                    {selected.invite_status === 'pending' && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 9 }}>
                        <button type="button" disabled={busy === selected.id}
                          onClick={() => inviteAction(selected, 'resend')}
                          style={{ padding: '5px 12px', background: '#fff', color: '#3a6ad9', border: '1px solid #bfdbfe', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                          Resend
                        </button>
                        <button type="button" disabled={busy === selected.id}
                          onClick={() => inviteAction(selected, 'cancel')}
                          style={{ padding: '5px 12px', background: '#fff', color: '#ff082f', border: '1px solid #ffc5ca', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    )}
                    {selected.invite_status === 'cancelled' && (
                      <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 6, lineHeight: 1.5 }}>
                        Withdrawn. The record is kept and this person can be invited again.
                      </div>
                    )}
                  </div>
                ) : !selected.closed_at && (
                  // 🚨 THE SAME ACTION THE INVITED LANE PERFORMS. Reaching that
                  // stage SENDS — setting it by hand was never a smaller version of
                  // doing it, it was the same action with the sending left out.
                  <div>
                    {/* ── 🚨 THE PARTNER TYPE DOES NOT GATE THE SEND ─────────────
                        This was disabled without a type, under "Set the partner
                        type first — it decides which agreement they sign."

                        🔑 IT DOES NOT. The recipient answers at pre-registration:
                        /doorlypro/start asks what they are, and THAT answer is
                        what registration stores and what papers them. The invite
                        value only ever pre-filled the question so it could be
                        skipped, and a rep who guessed wrong was silently
                        corrected by the person who knew.

                        So the gate did not prevent a wrong agreement — it made a
                        rep answer a question belonging to the recipient before
                        they were allowed to ask them anything.
                        utils/partnerInviteChecks took partner_type off REQUIRED
                        and components/PartnerInviteModal stopped asking; this
                        button and its route were the two surfaces still holding
                        the old belief, and they contradicted the other two. */}
                    <button type="button" disabled={busy === selected.id}
                      onClick={() => sendInvite(selected.id)}
                      style={{ padding: '9px 14px', background: '#6d28d9',
                        color: '#fff', border: 'none', borderRadius: 8,
                        fontSize: 13, fontWeight: 700,
                        cursor: busy === selected.id ? 'not-allowed' : 'pointer',
                        opacity: busy === selected.id ? 0.6 : 1 }}>
                      {busy === selected.id ? 'Sending…' : 'Send invitation'}
                    </button>
                    {selected.level === 'principal' && selected.partner_type && (
                      <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 5, lineHeight: 1.5 }}>
                        They&rsquo;ll get the account invitation, since they&rsquo;re setting up a firm.
                      </div>
                    )}
                  </div>
                )}

                {/* ⚠️ THE CALENDLY CONTROLS ARE COMMUNICATIONS TOO — a booked
                    call is something Doorly and this person arranged, and the
                    Join/Reschedule/Cancel links are how a rep acts on it. They
                    used to sit at the bottom under the dates, which is where
                    things go when nobody decides where they belong. */}
                {(selected.reschedule_url || selected.cancel_url) && (
                  <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 6 }}>
                      Discovery call {selected.start_time ? `· ${fmtWhen(selected.start_time)}` : ''}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {selected.join_url && <a href={selected.join_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#3a6ad9' }}>Join</a>}
                      {selected.reschedule_url && <a href={selected.reschedule_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#3a6ad9' }}>Reschedule</a>}
                      {selected.cancel_url && <a href={selected.cancel_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#ff082f' }}>Cancel</a>}
                    </div>
                  </div>
                )}
                </>)}

                {/* ── OVERVIEW, part two ─────────────────────────────────── */}
                {leadTab === 'overview' && (<>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>Type</label>
                    <select value={selected.partner_type || ''} onChange={(e) => patch(selected.id, { partner_type: e.target.value || null })}
                      style={{ ...sel, width: '100%', marginTop: 4 }}>
                      <option value="">Unknown</option>
                      {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>Level</label>
                    <select value={selected.level || ''} onChange={(e) => patch(selected.id, { level: e.target.value || null })}
                      style={{ ...sel, width: '100%', marginTop: 4 }}>
                      <option value="">Unknown</option>
                      {Object.entries(LEVEL_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>Owner</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                    {/* 🚨 THE CLAIM BUTTON IS GONE. It acted on unclaimed leads,
                        and a rep can no longer see one — so it was an affordance
                        with nothing left to act on, which is the dead-control
                        shape this project keeps finding.

                        🔑 REASSIGNMENT REPLACES IT, FOR ADMINS. That is a real
                        action on a real row: moving a lead from one rep to
                        another, which is what the owner question actually is now
                        that nothing arrives unowned. */}
                    {data?.canSeeAll ? (
                      <select value={selected.owner_id || ''} disabled={busy === selected.id}
                        onChange={(e) => patch(selected.id, { owner_id: e.target.value ? Number(e.target.value) : null })}
                        style={{ ...sel, flex: 1 }}>
                        <option value="">— Unassigned —</option>
                        {owners.map((o) => <option key={o.id} value={o.id}>{o.name || o.email}</option>)}
                      </select>
                    ) : (
                      <span style={{ fontSize: 13, color: selected.owner_name ? '#111827' : '#b45309', fontWeight: 600 }}>
                        {selected.owner_name || 'Unassigned'}
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>Notes</label>
                  {/* 🔑 SURFACED AND EDITABLE. The column existed and nothing ever
                      showed it, so every call note a rep took lived in their own
                      head or somebody else's inbox. */}
                  <textarea defaultValue={selected.notes || ''} rows={5}
                    onBlur={(e) => { if (e.target.value !== (selected.notes || '')) patch(selected.id, { notes: e.target.value }); }}
                    placeholder="What was said, what happens next…"
                    style={{ ...sel, width: '100%', marginTop: 4, resize: 'vertical', fontFamily: 'inherit' }} />
                </div>

                <button type="button" disabled={busy === selected.id}
                  onClick={() => patch(selected.id, { contacted: true })}
                  style={{ padding: '9px 14px', background: '#111827', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  {busy === selected.id ? 'Saving…' : 'Mark contacted just now'}
                </button>

                {/* ── 🔑 ARCHIVE, AND ITS REVERSE ─────────────────────────────
                    `not_interested` and `unresponsive` were the only way to
                    close a lead and both lived in the stage dropdown, which is
                    where a rep looks to move somebody FORWARD. Archiving is a
                    different kind of act and now has its own control.

                    ⚠️ IT IS REVERSIBLE AND SAYS SO. The server clears closed_at
                    when a lead moves off a terminal, which is what frees the
                    address for a future lead — the one-open-per-email index is
                    on closed_at IS NULL. A rep who archived somebody and then
                    heard back can carry on.

                    🚨 NOT OFFERED ON A CONVERTED LEAD. That row closed because
                    the partner registered; archiving it would overwrite the
                    outcome with a giving-up, and the stage dropdown already
                    refuses to set `registered` by hand for the same reason. */}
                {selected.stage !== 'registered' && (
                  selected.stage === 'archived' ? (
                    <button type="button" disabled={busy === selected.id}
                      onClick={() => patch(selected.id, { stage: 'new' })}
                      style={{ padding: '9px 14px', background: '#fff', color: '#3a6ad9', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                      {busy === selected.id ? 'Saving…' : 'Restore from archive'}
                    </button>
                  ) : (
                    <button type="button" disabled={busy === selected.id}
                      onClick={() => {
                        if (!window.confirm(`Archive ${selected.name || selected.email}? They stop appearing in the default list and can be restored.`)) return;
                        patch(selected.id, { stage: 'archived' });
                      }}
                      style={{ padding: '9px 14px', background: '#fff', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                      {busy === selected.id ? 'Saving…' : 'Archive this lead'}
                    </button>
                  )
                )}
                </>)}

                {/* ── ACTIVITY ───────────────────────────────────────────── */}
                {/* ⚠️ THE SOURCE LINE IS GONE FROM HERE. It was in this footer
                    AND is now the Source tab's whole subject; printing it twice
                    reads as two facts until you look closely. Dates only. */}
                {leadTab === 'activity' && (
                  <div style={{ fontSize: 12.5, color: '#374151', lineHeight: 2 }}>
                    <div>Created: {fmtDate(selected.created_at)}</div>
                    <div>Last contacted: {sinceLabel(selected.last_contacted_at).text}</div>
                    {selected.invite_sent_at && <div>Invited: {fmtDate(selected.invite_sent_at)}</div>}
                    {selected.start_time && <div>Discovery call: {fmtWhen(selected.start_time)}</div>}
                    {selected.drip_enrolled_at && <div>Drip enrolled: {fmtDate(selected.drip_enrolled_at)}</div>}
                    {selected.converted_at && <div>Converted: {fmtDate(selected.converted_at)}</div>}
                    {selected.closed_at && <div>Closed: {fmtDate(selected.closed_at)} — {selected.closed_reason}</div>}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ⚠️ THE SAME MODAL THE PARTNERS PAGE MOUNTS, not a second invite path.
            It posts to POST /admin/partners/invite, which writes the invite AND
            the lead behind it — so a lead appears here the moment it is sent. */}
        {/* ⚠️ MOUNTED ALWAYS, GATED BY `open` — the same contract the Partners
            page uses. The modal closes itself 2.5s after a send so the confirmation
            is readable; unmounting it on onSent would cut that short. */}
        <PartnerInviteModal
          token={token}
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          onSent={load}
        />
        {importOpen && (
          <LeadImportModal
            onClose={() => setImportOpen(false)}
            onCommitted={() => { setImportOpen(false); load(); }}
          />
        )}
      </div>
    </AdminLayout>
  );
}
